import {
  FileUploaderRegular,
  OutputCollectionState,
  OutputCollectionStatus,
  UploadCtxProvider,
} from "@uploadcare/react-uploader";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import Avatar from "../../../components/Avatar";
import { Card } from "../../../components/Card";
import { Button, Submit } from "../../../components/CTA";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import Loading from "../../../components/Loading";
import { View } from "../../../components/View";
import {
  useGetBrandGroupQuery,
  useUpdateBrandGroupMutation,
} from "../../../graphql/generated";
import useAnalytics from "../../../hooks/useAnalytics";
import useGqlClient from "../../../hooks/useGqlClient";
import { authSelectors } from "../../../store/auth/selector";
import styled from "../../../styles";

export const GroupProfile = () => {
  const { track } = useAnalytics();
  const [name, setName] = useState("");
  const [logo, setLogo] = useState<string | null | undefined>("");
  const client = useGqlClient();
  const { mutate: updateBrandGroup, isLoading: isUpdatingBrandGroup } =
    useUpdateBrandGroupMutation(client, {});
  const activeGroupId = useSelector(authSelectors.activeGroupId);
  const uploaderRef = useRef<InstanceType<UploadCtxProvider> | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useGetBrandGroupQuery(client, {
    id: activeGroupId ? activeGroupId : "",
  });

  useEffect(() => {
    if (data?.brandGroup) {
      setName(data.brandGroup.name);
      setLogo(data.brandGroup.logoUrl);
    }
  }, [data]);

  const handleImageUpload = (
    files: OutputCollectionState<OutputCollectionStatus, "maybe-has-group">
  ) => {
    console.log(files);
    if (files.successEntries.length > 0) {
      const newImages = [];
      for (const file of files.successEntries) {
        console.log("file: ", file);
        if (file.cdnUrl) {
          newImages.push(file.cdnUrl);
        }
      }
      setLogo(newImages[0]);
      updateBrandGroup(
        {
          input: {
            logo: newImages[0],
          },
        },
        {
          onError: (e: any) => {
            console.error(e);
          },
          onSuccess: (res: any) => {
            toast.success("Logo updated");
            track("Logo updated", {
              id: activeGroupId ? activeGroupId : undefined,
              brand: name,
            });
            queryClient.invalidateQueries(["Accounts"], {
              exact: false,
            });
          },
        }
      );
    }

    uploaderRef.current?.api.removeAllFiles();
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!data?.brandGroup) {
      return;
    }

    if (name === data.brandGroup.name && logo === data.brandGroup.logoUrl) {
      return;
    }

    updateBrandGroup(
      {
        input: {
          name: name,
        },
      },
      {
        onError: (e: any) => {
          console.error(e);
        },
        onSuccess: (res: any) => {
          toast.success("Brand group profile updated");
          track("Brand group updated", {
            id: activeGroupId ? activeGroupId : undefined,
            brand: name,
          });
          queryClient.invalidateQueries(["Accounts"], {
            exact: false,
          });
        },
      }
    );
  };

  if (!data?.brandGroup || !activeGroupId || isLoading) {
    return <Loading />;
  }

  return (
    <StyledCard>
      <form onSubmit={onSubmit}>
        <View margin="0 0 xl 0">
          <Flex dir="row" align="center">
            <View margin="xs 0 0">
              <Avatar
                style={{ cursor: "unset" }}
                withShadow={false}
                borderSize={0}
                size={52}
                url={
                  logo
                    ? logo
                    : "https://wenibble-images.s3.eu-central-1.amazonaws.com/tiktok_avatars/empty.jpeg"
                }
              />
            </View>
            <div>
              <View margin="0 0 0 xl">
                <Flex>
                  <div>
                    <Button
                      size="xs"
                      buttonType="secondary"
                      onClick={() => uploaderRef.current?.api.initFlow()}
                    >
                      Upload Logo
                    </Button>
                  </div>
                </Flex>
              </View>
            </div>
          </Flex>
        </View>
        <Input
          label="Group Name"
          defaultValue={data.brandGroup.name}
          name="name"
          onChange={(e) => setName(e.target.value)}
          margin="0 0 xl 0"
        />
        <Submit
          type="submit"
          value={isUpdatingBrandGroup ? "Loading..." : "Save"}
          margin="xl 0 0 0"
        />
        <StyledFileUploaderRegular
          pubkey="04ec353cf089294c8374"
          apiRef={uploaderRef}
          sourceList="local,url,facebook,instagram,gdrive,gphotos,dropbox,onedrive"
          imageShrink="512x512"
          imgOnly
          cropPreset="1:1"
          multiple={false}
          onDoneClick={handleImageUpload}
          classNameUploader="uploader"
        />
      </form>
    </StyledCard>
  );
};

const StyledFileUploaderRegular = styled(FileUploaderRegular)`
  .uploader {
    [uc-simple-btn] {
      display: none;
    }
  }
`;

const StyledCard = styled(Card)`
  margin: 1px;
  margin-top: ${(p) => p.theme.spacing.l};
  padding: ${(p) => p.theme.spacing.l};
`;
